import React, { useState, useEffect, useRef, useMemo } from "react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AgGridReact } from "ag-grid-react";
import moment from "moment/moment";
import "../pagination/pagination.css";
import Pagination from "../pagination/pagination";
import Select from "react-select";
import Modal from 'react-bootstrap/Modal';
import { RiRefreshLine } from "react-icons/ri";
import { FaFileCircleExclamation } from "react-icons/fa6";
import LeaseApiService from "../../services/apiService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { ClipLoader } from 'react-spinners';
import { toast } from "react-toastify";
import { useCurrency } from '../../../INVENTORY/Layouts/currencyContext';
import { AsyncPaginate } from 'react-select-async-paginate';
import * as XLSX from "xlsx";
import { useTranslation } from 'react-i18next';
import "../customerMaster/customerListing.css";
import "./reports.css";
import { useReportCards } from "../shared/reportCards";

const AccruedRentReport = () => {
    const { t } = useTranslation(['reports', 'common']);
    const apiService = new LeaseApiService();
    const ITEMS_PER_PAGE = 100
    const gridRef = useRef();
    const { currencyPrecision } = useCurrency();
    const [rowData, setRowData] = useState([]);
    const [installmentsAll, setInstallmentsAll] = useState([]);
    const [exportSummary, setExportSummary] = useState([]);
    const [exportInstallments, setExportInstallments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageSize, setPageSize] = useState(50);
    const [pagination, setPagination] = useState();
    const [FilterTab, setFilterTab] = useState(true);
    const [propertyList, setPropertyList] = useState([]);
    const [leaseList, setLeaseList] = useState([]);
    const [tanentList, setTanentList] = useState([]);
    const [loader, setLoader] = useState(false);
    const [showInstallmentModal, setShowInstallmentModal] = useState(false);
    const [selectedLease, setSelectedLease] = useState(null);
    const [formData, setFormData] = useState({
        property: { label: "All", value: "All" },
        tenantId: "",
        tenantName: "",
        leaseNo: "",
        asOnDate: "",
    })
    const formatDate = (date) => {
        return moment(date).format("DD-MMM-YYYY");
    };
    useEffect(() => {
        fetchDropdownData();
        setFormData((prev) => ({
            ...prev,
            asOnDate: formatDate(new Date()),
        }));
    }, []);

    const fetchDropdownData = async () => {
        try {
            const params = new URLSearchParams();
            params.append('dropdownTypes', 'LRAgreeSearchProperties,LRSearchLeaseNos,LRAgreeSearchCustomers,');
            const res = await apiService.getApiParamater('REPORT_MULTI_DROP', `?${params.toString()}`);
            const addAllOption = (list) => [{ value: 'All', text: 'All' }, ...list || []];
            setPropertyList(res?.data?.dropdowns?.LRAgreeSearchProperties);
            setLeaseList(addAllOption(res?.data?.dropdowns?.LeaseNo));
            setTanentList(addAllOption(res?.data?.dropdowns?.LRAgreeSearchCustomers));
        } catch (error) {
            console.error('Error fetching dropdown data:', error);
        }
    }
    const dateFormatter = (params) => {
        if (!params.value) return '';
        const date = moment(params.value);
        return date.isValid() ? date.format('DD-MMM-YYYY') : '';
    };
    const dateComparator = (date1, date2) => {
        const momentDate1 = moment(date1);
        const momentDate2 = moment(date2);
        return momentDate1.isSame(momentDate2) ? 0 : momentDate1.isBefore(momentDate2) ? -1 : 1;
    };
    const amountFormatter = (params) => {
        if (params?.value === null || params?.value === undefined) return '';
        return Number(params.value).toFixed(currencyPrecision?.decimalNumbers || 2);
    };
    const buildParams = (newPageNo, size) => {
        const params = new URLSearchParams();
        params.append("AsOnDate", formData?.asOnDate || formatDate(new Date()));
        params.append("PropertyName", formData?.property?.label || "All");
        params.append("LeaseNo", formData?.leaseNo?.label || "All");
        params.append("TenantNo", formData?.tenantId?.label || formData?.tenantName?.value?.value || "All");
        params.append("PageNo", newPageNo || 1);
        params.append("PageSize", size);
        return params;
    }
    const getItems = async (newPageNo = pagination?.currentPage) => {
        try {
            setLoader(true)
            const params = buildParams(newPageNo, pageSize || 50);
            const res = await apiService.getApiParamater("REPORT_ACCRUED_RENT", `?${params.toString()}`);
            const summary = res?.data?.summary || res?.data?.result?.summary || [];
            const installments = res?.data?.installments || res?.data?.result?.installments || [];
            const paginationData = res?.data?.paginationData || res?.data?.result?.paginationData;
            if (summary?.length) {
                setRowData(summary);
                setInstallmentsAll(installments);
                setPagination(paginationData);
                getAllItems(paginationData)
                setLoading(false);
                setLoader(false)
            } else {
                setRowData([]);
                setInstallmentsAll([]);
                toast.error(res?.data?.message)
                setLoader(false)
                setLoading(false);
            }
        } catch (error) {
            console.error("Error fetching items:", error);
            setLoader(false)
            setLoading(false);
        }
    }
    const getAllItems = async (total) => {
        try {
            const params = buildParams(1, total?.totalRecords);
            const res = await apiService.getApiParamater("REPORT_ACCRUED_RENT", `?${params.toString()}`);
            const summary = res?.data?.summary || res?.data?.result?.summary || [];
            const installments = res?.data?.installments || res?.data?.result?.installments || [];
            setExportSummary(summary);
            setExportInstallments(installments);
        } catch (error) {
            console.error("Error fetching items:", error);
        }
    }
    const openInstallmentModal = (row) => {
        setSelectedLease(row);
        setShowInstallmentModal(true);
    }
    const installmentRows = useMemo(() => {
        if (!selectedLease) return [];
        return installmentsAll.filter(inst => inst.leaseNo === selectedLease.leaseNo);
    }, [installmentsAll, selectedLease]);
    const columnDefs = [
        {
            headerName: t('reports:accruedRent.columns.leaseNo'), field: "leaseNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130, pinned: "left", cellRenderer: (params) => {
                return (
                    <span
                        style={{
                            color: "#1a73e8",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: 500,
                        }}
                        onClick={() => openInstallmentModal(params.data)}
                    >
                        {params.value}
                    </span>
                );
            },
        },
        { headerName: t('reports:accruedRent.columns.property'), field: "property_Name", filter: true, floatingFilter: true, suppressMovable: true, width: 200, pinned: "left" },
        { headerName: t('reports:accruedRent.columns.unitNo'), field: "unit_No", filter: true, floatingFilter: true, suppressMovable: true, width: 100, pinned: "left" },
        { headerName: t('reports:accruedRent.columns.tenantNo'), field: "tenantNo", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: t('reports:accruedRent.columns.tenantName'), field: "tenantName", filter: true, floatingFilter: true, suppressMovable: true, width: 200 },
        {
            headerName: t('reports:accruedRent.columns.annualRent'), field: "annualRent", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.rentDueTillDate'), field: "rentDueTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.vatDueTillDate'), field: "vatDueTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.stDueTillDate'), field: "stDueTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.totalDueTillDate'), field: "totalRentDueTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 160,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.receiptTillDate'), field: "receiptTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.balanceTillDate'), field: "balanceTillDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.columns.lastReceiptDate'), field: "lastReceiptDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        { headerName: t('reports:accruedRent.columns.paymentStatus'), field: "paymentStatus", filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
    ]
    const installmentColumnDefs = [
        { headerName: t('reports:accruedRent.installmentColumns.installmentDesc'), field: "installment_Desc", filter: true, floatingFilter: true, suppressMovable: true, width: 200, pinned: "left" },
        { headerName: t('reports:accruedRent.installmentColumns.percentage'), field: "percentage", filter: true, floatingFilter: true, suppressMovable: true, width: 110 },
        {
            headerName: t('reports:accruedRent.installmentColumns.dueDate'), field: "dueDate", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.rentAmount'), field: "rentAmount", filter: true, floatingFilter: true, suppressMovable: true, width: 130,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.vat'), field: "vat", filter: true, floatingFilter: true, suppressMovable: true, width: 110,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.st'), field: "st", filter: true, floatingFilter: true, suppressMovable: true, width: 110,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.installmentTotal'), field: "installmentTotal", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.receiptAmount'), field: "receiptAmount", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.balanceAmount'), field: "balanceAmount", filter: true, floatingFilter: true, suppressMovable: true, width: 140,
            valueFormatter: amountFormatter,
        },
        {
            headerName: t('reports:accruedRent.installmentColumns.lastPaymentDate'), field: "lastPaymentDate", filter: true, floatingFilter: true, suppressMovable: true, width: 150,
            valueFormatter: dateFormatter,
            comparator: dateComparator,
        },
        { headerName: t('reports:accruedRent.installmentColumns.receiptCount'), field: "receiptCount", filter: true, floatingFilter: true, suppressMovable: true, width: 130 },
        { headerName: t('reports:accruedRent.installmentColumns.paymentStatus'), field: "paymentStatus", filter: true, floatingFilter: true, suppressMovable: true, width: 140 },
        { headerName: t('reports:accruedRent.installmentColumns.ageDays'), field: "ageDays", filter: true, floatingFilter: true, suppressMovable: true, width: 110 },
    ]
    const handleClearFilters = () => {
        setRowData([])
        setInstallmentsAll([])
        setFormData({
            property: { label: "All", value: "All" },
            tenantId: "",
            tenantName: "",
            leaseNo: "",
            asOnDate: formatDate(new Date()),
        });
    }
    const handleDropDownChange = async ({ name, value }) => {
        if (name === "tenantId") {
            setFormData(prev => ({ ...prev, [name]: value, tenantName: { label: value?.value?.text, value: value?.value } }));
        } else if (name === "tenantName") {
            setFormData(prev => ({ ...prev, [name]: value, tenantId: { label: value?.value?.value, value: value?.value } }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    }
    const handleDateChange = async (input) => {
        const name = input?.target?.name || input.name;
        const value = input?.target?.value || input.value;

        const updatedValue = typeof value === 'string' ? value : formatDate(value);
        let updatedDates = { ...formData, [name]: updatedValue };
        setFormData(updatedDates);
    }
    const loadOptions = (search, loadedOptions, { page }) => {
        let filtered = tanentList;

        if (search) {
            filtered = tanentList.filter(tenant =>
                tenant.value.toLowerCase().includes(search.toLowerCase()) ||
                tenant.text.toLowerCase().includes(search.toLowerCase())
            );
        }

        const start = page * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;

        const optionsChunk = filtered.slice(start, end);

        return Promise.resolve({
            options: optionsChunk.map(tenant => ({ value: tenant, label: tenant.value })),
            hasMore: end < filtered.length,
            additional: { page: page + 1 }
        });
    }
    const loadOptionsName = (search, loadedOptions, { page }) => {
        let filtered = tanentList;

        if (search) {
            filtered = tanentList.filter(tenant =>
                tenant.value.toLowerCase().includes(search.toLowerCase()) ||
                tenant.text.toLowerCase().includes(search.toLowerCase())
            );
        }

        const start = page * ITEMS_PER_PAGE;
        const end = start + ITEMS_PER_PAGE;

        const optionsChunk = filtered.slice(start, end);

        return Promise.resolve({
            options: optionsChunk.map(tenant => ({ value: tenant, label: tenant.text })),
            hasMore: end < filtered.length,
            additional: { page: page + 1 }
        });
    }
    const normalizeValue = (value) => {
        if (value === null || value === undefined) return "";
        if (value === 0 || value === "0") return "";

        if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
            return moment(value).format("DD-MMM-YYYY");
        }

        return value;
    };
    const summaryExportColumns = [
        { header: "Lease No", dataKey: "leaseNo" },
        { header: "Property", dataKey: "property_Name" },
        { header: "Unit No", dataKey: "unit_No" },
        { header: "Tenant No", dataKey: "tenantNo" },
        { header: "Tenant Name", dataKey: "tenantName" },
        { header: "Annual Rent", dataKey: "annualRent" },
        { header: "Rent Due Till Date", dataKey: "rentDueTillDate" },
        { header: "VAT Due Till Date", dataKey: "vatDueTillDate" },
        { header: "ST Due Till Date", dataKey: "stDueTillDate" },
        { header: "Total Due Till Date", dataKey: "totalRentDueTillDate" },
        { header: "Receipt Till Date", dataKey: "receiptTillDate" },
        { header: "Balance Till Date", dataKey: "balanceTillDate" },
        { header: "Last Receipt Date", dataKey: "lastReceiptDate" },
        { header: "Payment Status", dataKey: "paymentStatus" },
    ];
    const installmentExportColumns = [
        { header: "Lease No", dataKey: "leaseNo" },
        { header: "Property", dataKey: "property_Name" },
        { header: "Unit No", dataKey: "unit_No" },
        { header: "Tenant No", dataKey: "tenantNo" },
        { header: "Tenant Name", dataKey: "tenantName" },
        { header: "Installment Desc", dataKey: "installment_Desc" },
        { header: "Percentage", dataKey: "percentage" },
        { header: "Due Date", dataKey: "dueDate" },
        { header: "Rent Amount", dataKey: "rentAmount" },
        { header: "VAT", dataKey: "vat" },
        { header: "ST", dataKey: "st" },
        { header: "Installment Total", dataKey: "installmentTotal" },
        { header: "Receipt Amount", dataKey: "receiptAmount" },
        { header: "Balance Amount", dataKey: "balanceAmount" },
        { header: "Last Payment Date", dataKey: "lastPaymentDate" },
        { header: "Receipt Count", dataKey: "receiptCount" },
        { header: "Payment Status", dataKey: "paymentStatus" },
        { header: "Age Days", dataKey: "ageDays" },
    ];
    const exportPDF = () => {
        if (!exportSummary?.length) return;

        const doc = new jsPDF("landscape", "pt", "A4");

        doc.setFillColor(110, 81, 153);
        doc.rect(0, 0, doc.internal.pageSize.width, 50, "F");

        doc.setTextColor(255);
        doc.setFontSize(16);
        doc.text("Accrued Rent Report", 40, 32);

        doc.setFontSize(9);
        doc.text(
            `As on: ${formData?.asOnDate}`,
            doc.internal.pageSize.width - 160,
            32
        );

        doc.setTextColor(0);

        doc.autoTable({
            startY: 60,
            head: [summaryExportColumns.map(col => col.header)],
            body: exportSummary.map(row =>
                summaryExportColumns.map(col => normalizeValue(row[col.dataKey]))
            ),
            styles: {
                fontSize: 8,
                cellPadding: 5,
                valign: "middle",
            },
            headStyles: {
                fillColor: [110, 81, 153],
                textColor: 255,
                fontStyle: "bold",
                halign: "center",
            },
            bodyStyles: {
                halign: "center",
            },
            alternateRowStyles: {
                fillColor: [240, 237, 245],
            },
            didDrawPage: data => {
                doc.setFontSize(8);
                doc.text(
                    `Page ${data.pageNumber}`,
                    doc.internal.pageSize.width / 2,
                    doc.internal.pageSize.height - 10,
                    { align: "center" }
                );
            },
        });
        doc.save("Accrued_Rent_Report.pdf");
    };
    const exportExcel = () => {
        if (!exportSummary?.length && !exportInstallments?.length) return;

        const summaryData = exportSummary.map(item => {
            const row = {};
            summaryExportColumns.forEach(col => {
                row[col.header] = normalizeValue(item[col.dataKey]);
            });
            return row;
        });
        const installmentData = exportInstallments.map(item => {
            const row = {};
            installmentExportColumns.forEach(col => {
                row[col.header] = normalizeValue(item[col.dataKey]);
            });
            return row;
        });

        const workbook = XLSX.utils.book_new();
        const summarySheet = XLSX.utils.json_to_sheet(summaryData);
        const installmentSheet = XLSX.utils.json_to_sheet(installmentData);
        XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
        XLSX.utils.book_append_sheet(workbook, installmentSheet, "Installments");
        XLSX.writeFile(workbook, "Accrued_Rent_Report.xlsx");
    };
    const cards = useReportCards({
        label: "Leases in view",
        rowData,
        pagination,
        fields: [["paymentStatus",3],["property_Name",1]],
    });

    return (
        <div className="cl-page rpt-legacy">
            <div className="cl-titlebar">
                <div className="cl-title">
                    <h1>{t('reports:accruedRent.title')}</h1>
                    <p>{t('reports:accruedRent.description', 'Accrued rent per lease for the selected period.')}</p>
                </div>
            </div>
            {cards.strip}
            <div className="cost-center-section">
                <div
                    className="d-flex mt-3 cursor-pointer slide-header"
                    onClick={() => setFilterTab(!FilterTab)}
                >
                    <div className="h6 pt-1">{t('common:searchParameters')}</div>
                </div>
                <div className={`slide-content ${FilterTab ? "open" : "closed"}`}>
                    <div className={`border-box2 p-3 ${FilterTab ? "slide-in" : "slide-out"}`}>
                        <div className="form-group grid-contain ">
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:property')} </label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.property}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'property', value: selectedOption })}
                                    options={propertyList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('reports:tenantId')}</label>
                                <AsyncPaginate
                                    className="p-0 w-100"
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5" : null,
                                            color: "#000",
                                            ":hover": { backgroundColor: "#F5F5F5", color: "#000" }
                                        }),
                                    }}
                                    value={formData?.tenantId}
                                    loadOptions={loadOptions}
                                    onChange={(selectedOption) => handleDropDownChange({ name: "tenantId", value: selectedOption })}
                                    additional={{ page: 0 }}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:leaseNo')}</label>
                                <Select
                                    className="p-0 w-100"
                                    theme={(theme) => ({
                                        ...theme,
                                        borderRadius: 5,
                                        padding: 9,
                                    })}
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5 " : null,
                                            color: state.isFocused ? "#000 " : "#000",
                                            ":hover": {
                                                backgroundColor: "#F5F5F5",
                                                color: "#000 ",
                                            },
                                        }),
                                    }}
                                    value={formData?.leaseNo}
                                    onChange={(selectedOption) => handleDropDownChange({ name: 'leaseNo', value: selectedOption })}
                                    options={leaseList?.map(country => ({ value: country, label: country?.text }))}
                                />
                            </div>
                            <div className="d-flex">
                                <label htmlFor="validationCustom18" className="form-label">{t('common:tenant')}</label>
                                <AsyncPaginate
                                    className="p-0 w-100"
                                    menuPortalTarget={document.body}
                                    isClearable
                                    styles={{
                                        menuPortal: base => ({ ...base, zIndex: 9999 }),
                                        option: (provided, state) => ({
                                            ...provided,
                                            backgroundColor: state.isFocused ? "#F5F5F5" : null,
                                            color: "#000",
                                            ":hover": { backgroundColor: "#F5F5F5", color: "#000" }
                                        }),
                                    }}
                                    value={formData?.tenantName}
                                    loadOptions={loadOptionsName}
                                    onChange={(selectedOption) => handleDropDownChange({ name: "tenantName", value: selectedOption })}
                                    additional={{ page: 0 }}
                                />
                            </div>
                            <div className="d-flex">
                                <label className="form-label">{t('common:asOnDate')}</label>
                                <DatePicker
                                    selected={formData.asOnDate}
                                    onChange={(date) => handleDateChange({ name: 'asOnDate', value: date })}
                                    dateFormat="dd-MMM-yyyy"
                                    className="p-2 form-control w-100"
                                />
                            </div>
                            <div className="d-flex mt-2 gap-1">
                                {loader === true ?
                                    <div className='mainbtnGry flex-end gap-2 w-50'>
                                        <ClipLoader color="#ffffff" className='mt-1' size={25} />
                                    </div>
                                    :
                                    <div
                                        onClick={() => getItems()}
                                        className="mainbtnGry flex-end gap-2 w-50"
                                    >
                                        <FaFileCircleExclamation
                                            color="#fff"
                                            className="center mt-2 mx-2"
                                        />
                                        <div className="center">{t('common:search')}</div>
                                    </div>
                                }
                                <div
                                    onClick={() => handleClearFilters()}
                                    className="mainbtnRed flex-end gap-2 w-50"
                                >
                                    <RiRefreshLine color="#fff" className="center mt-2 mx-2" />
                                    <div className="center">{t('common:reset')}</div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
            <div className="border-box2 p-3">
                {loading ? (
                    <div>{t('common:loading')}</div>
                ) : (
                    <>
                        <div className="d-flex justify-content-between">
                            <h5 className="col mainInvHead">{t('reports:accruedRent.title')}</h5>
                            <button className="btn btn-secondary mx-3" onClick={exportExcel}>
                                {t('reports:exportAsExcel')}
                            </button>
                        </div>

                        <div className="gridshad mt-3">
                            <div className="ag-theme-quartz" style={{ height: 650 }}>
                                <AgGridReact
                                    ref={gridRef}
                                    rowData={cards.shownRows}
                                    enableFilter={true}
                                    floatingFilter={true}
                                    columnDefs={columnDefs}
                                    onCellClicked={(params) => {
                                        if (params.colDef.field === "leaseNo") {
                                            openInstallmentModal(params.data);
                                        }
                                    }}
                                />
                            </div>
                            {!cards.active && <Pagination
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pagination={pagination}
                                getItems={getItems}
                            />}
                        </div>
                    </>
                )}
            </div>
            <Modal show={showInstallmentModal} size="xl" onHide={() => setShowInstallmentModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {t('reports:accruedRent.installmentsTitle')} - {selectedLease?.leaseNo} ({selectedLease?.tenantName})
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="ag-theme-quartz" style={{ height: 450 }}>
                        <AgGridReact
                            rowData={installmentRows}
                            enableFilter={true}
                                    floatingFilter={true}
                            columnDefs={installmentColumnDefs}
                            getRowStyle={(params) => (params.node.rowIndex % 2 === 0 ? { background: "#F0EDF5" } : { background: "#fff" })}
                        />
                    </div>
                </Modal.Body>
            </Modal>
        </div >
    );
};
export default AccruedRentReport;
